<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'CSAT Detalhado',
  },
});

const distribution = computed(() => props.data?.distribution || {});
const distributionPercentage = computed(
  () => props.data?.distribution_percentage || {}
);
const trend = computed(() => props.data?.trend || []);

const emojis = {
  excellent: '😄',
  good: '🙂',
  neutral: '😐',
  poor: '😕',
  bad: '😞',
};

const colors = {
  excellent: 'bg-n-teal-9',
  good: 'bg-n-blue-9',
  neutral: 'bg-n-amber-9',
  poor: 'bg-n-orange-9',
  bad: 'bg-n-ruby-9',
};

const labels = {
  excellent: 'Excelente (5)',
  good: 'Bom (4)',
  neutral: 'Neutro (3)',
  poor: 'Ruim (2)',
  bad: 'Péssimo (1)',
};
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-star text-lg text-n-amber-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-2">
        <span
          class="text-2xl font-bold"
          :class="
            data.satisfaction_score >= 80
              ? 'text-n-teal-11'
              : data.satisfaction_score >= 60
                ? 'text-n-amber-11'
                : 'text-n-ruby-11'
          "
        >
          {{ data.satisfaction_score || 0 }}%
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Summary Stats -->
      <div class="grid grid-cols-3 gap-3 text-center">
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-2xl font-bold text-n-slate-12">
            {{ data.total_responses || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Respostas</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div class="text-2xl font-bold text-n-amber-11">
            {{ data.average_rating || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Média (1-5)</div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1">
          <div
            class="text-2xl font-bold"
            :class="
              data.nps_score >= 50
                ? 'text-n-teal-11'
                : data.nps_score >= 0
                  ? 'text-n-amber-11'
                  : 'text-n-ruby-11'
            "
          >
            {{ data.nps_score || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">NPS Score</div>
        </div>
      </div>

      <!-- Distribution -->
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">Distribuição</span>
        <div
          v-for="(key, idx) in ['excellent', 'good', 'neutral', 'poor', 'bad']"
          :key="key"
          class="flex items-center gap-2"
        >
          <span class="text-lg">{{ emojis[key] }}</span>
          <span class="text-xs text-n-slate-11 w-20">{{ labels[key] }}</span>
          <div class="flex-1 h-4 bg-n-alpha-1 rounded-full overflow-hidden">
            <div
              class="h-full transition-all duration-300"
              :class="colors[key]"
              :style="{ width: `${distributionPercentage[key] || 0}%` }"
            />
          </div>
          <span class="text-xs text-n-slate-11 w-12 text-right">
            {{ distribution[key] || 0 }} ({{
              distributionPercentage[key] || 0
            }}%)
          </span>
        </div>
      </div>

      <!-- Top Agents -->
      <div v-if="data.by_agent?.length > 0" class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">Top Agentes por CSAT</span>
        <div class="flex flex-col gap-1">
          <div
            v-for="agent in data.by_agent.slice(0, 5)"
            :key="agent.agent_id"
            class="flex items-center justify-between py-1 px-2 rounded bg-n-alpha-1"
          >
            <span class="text-sm text-n-slate-12">{{ agent.agent_name }}</span>
            <span
              class="text-sm font-medium"
              :class="
                agent.satisfaction_score >= 80
                  ? 'text-n-teal-11'
                  : agent.satisfaction_score >= 60
                    ? 'text-n-amber-11'
                    : 'text-n-ruby-11'
              "
            >
              {{ agent.satisfaction_score }}%
            </span>
          </div>
        </div>
      </div>

      <!-- Feedback Stats -->
      <div
        v-if="data.feedback"
        class="flex items-center justify-between text-xs text-n-slate-11 pt-2 border-t border-n-weak"
      >
        <span>{{ data.feedback.total_with_feedback }} com feedback escrito</span>
        <span>{{ data.feedback.feedback_rate }}% taxa de feedback</span>
      </div>
    </div>
  </div>
</template>
